// BucketsPanel.jsx — the non-field buckets as a tabbed side panel (§6).
//
// Native fields (won't create — FMX has these), Duplicates (already on this
// site, deterministic — computed from the live validation results), Prereq-
// uisites (blockers), Unsupported logic (API can't create; carries a manual
// follow-up note that rides through to results in M3), Flags (needs a human
// decision), and Couldn't interpret (quarantined entries).
//
// Every AI-bucket item has two actions: dismiss (reason kept in the session
// record) or promote — "create as a custom field anyway" moves it into the
// form as a draft field. Dismissed items stay visible in a collapsed list
// with a restore action; promoted items link to the field they became.
// Duplicates are different: they ARE draft fields already, so their actions
// are "go to the field" (to rename or review) or "remove from form" (skip).

import { useMemo, useState } from 'react';
import { T } from '../../theme';
import { Badge } from '../fmxkit';
import { bucketItems, bucketItemName } from '../../fieldBuilder/draftOps';
import { fieldsetLabel } from '../../fieldBuilder/fieldsets';
import { composePrereq } from '../../fieldBuilder/prereqs';
import { formulaChecklist } from '../../fieldBuilder/visibilityFormula';
import CopyFormulaButton from './CopyFormulaButton';

const BUCKETS = [
  { id: 'nativeMappings',   label: 'Native',    icon: 'building-shield' },
  { id: 'formulas',         label: 'Formulas',  icon: 'eye' },
  { id: 'conflicts',        label: 'Conflicts', icon: 'object-ungroup' },
  { id: 'duplicates',       label: 'Duplicates', icon: 'clone' },
  { id: 'prerequisites',    label: 'Prereqs',   icon: 'list-check' },
  { id: 'unsupportedLogic', label: 'Logic',     icon: 'code-branch' },
  { id: 'flags',            label: 'Flags',     icon: 'flag' },
  { id: 'quarantined',      label: 'Unreadable', icon: 'circle-question' },
];

// Buckets that aren't AI-emitted item lists — they're derived from the draft
// and get their own renderers below.
const DERIVED_BUCKETS = new Set(['duplicates', 'formulas', 'conflicts']);

// Prerequisite copy is composed by prereqs.js (action-first, screened
// against the site's live names); the other buckets keep simple phrasing.
function itemDisplay(bucket, item, liveNames) {
  if (bucket === 'prerequisites') {
    const pre = composePrereq(item, liveNames);
    return { headline: pre.headline, detail: pre.detail, hint: pre.hint, kindLabel: pre.kindLabel, status: pre.status };
  }
  switch (bucket) {
    case 'nativeMappings':   return { headline: item.label, detail: `FMX already captures this natively (${item.mapsTo}). Won’t create.` };
    case 'unsupportedLogic': return { headline: `“${item.text}”`, detail: `The API can’t create ${item.kind === 'calculatedValue' ? 'calculated values' : 'conditional visibility'}. Manual follow-up: ${item.followUp || 'configure in FMX after creating the fields.'}` };
    case 'flags':            return { headline: item.issue, detail: item.suggestion ? `Suggestion: ${item.suggestion}` : null };
    case 'quarantined':      return { headline: `Couldn’t interpret (stage ${item.stage ?? '?'})`, detail: item.reason };
    default:                 return { headline: bucketItemName(bucket, item), detail: null };
  }
}

const PREREQ_STATUS_BADGE = {
  found:   { color: 'green', icon: 'check', text: 'on site' },
  missing: { color: 'red',   icon: 'circle-exclamation', text: 'missing' },
};

// One open bucket item with its Dismiss / Add-as-field actions. Dismissing
// reveals an inline reason input — no prompts, no modals on this screen.
function BucketItemCard({ bucket, entry, confirmedFieldsets, liveNames, onDismiss, onPromote, onJumpToField }) {
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const [fieldset, setFieldset] = useState(confirmedFieldsets[0] || '');
  const { key, item, status } = entry;
  const display = itemDisplay(bucket, item, liveNames);
  const statusBadge = PREREQ_STATUS_BADGE[display.status];

  if (status === 'dismissed') {
    return (
      <div style={{ padding: '7px 10px', borderBottom: `1px solid ${T.borderSubtle}`, background: T.bg }}>
        <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft, textDecoration: 'line-through' }}>
          {display.headline}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <span style={{ font: `400 11px/15px ${T.fontSans}`, color: T.inkSoft, flex: 1 }}>
            Dismissed{entry.reason ? ` — ${entry.reason}` : ''}
          </span>
          <button className="fmx-btn-xs" onClick={() => onDismiss(bucket, key, null, true)}>Restore</button>
        </div>
      </div>
    );
  }

  if (status === 'promoted') {
    return (
      <div style={{ padding: '7px 10px', borderBottom: `1px solid ${T.borderSubtle}`, background: T.tint.success }}>
        <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.ink }}>{display.headline}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <span style={{ font: `400 11px/15px ${T.fontSans}`, color: T.greenText, flex: 1 }}>
            <i className="fa-solid fa-check" /> In the form as a draft field
          </span>
          {entry.promotedFieldId && (
            <button className="fmx-btn-xs" onClick={() => onJumpToField(entry.promotedFieldId)}>Go to field</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderSubtle}` }}>
      {(display.kindLabel || statusBadge) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          {display.kindLabel && <Badge color="blueLight">{display.kindLabel}</Badge>}
          {statusBadge && <Badge color={statusBadge.color} icon={statusBadge.icon}>{statusBadge.text}</Badge>}
          {item.sourcePage != null && (
            <span style={{ font: `400 10px/14px ${T.fontSans}`, color: T.inkSoft }}>p.{item.sourcePage}</span>
          )}
        </div>
      )}
      <div style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink }}>{display.headline}</div>
      {display.detail && (
        <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>{display.detail}</div>
      )}
      {display.hint && (
        <div style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkSoft, marginTop: 3 }}>
          <i className="fa-solid fa-location-dot" /> {display.hint}
        </div>
      )}
      {bucket === 'quarantined' && (
        <div style={{ font: `400 10px/14px ${T.fontData}`, color: T.inkSoft, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {JSON.stringify(item.raw)}
        </div>
      )}

      {!dismissing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <button className="fmx-btn-xs" onClick={() => setDismissing(true)}>Dismiss</button>
          {confirmedFieldsets.length > 1 && (
            <select className="fmx-select" style={{ width: 'auto', maxWidth: 170 }} value={fieldset} onChange={e => setFieldset(e.target.value)}>
              {confirmedFieldsets.map(fs => <option key={fs} value={fs}>{fieldsetLabel(fs)}</option>)}
            </select>
          )}
          <button className="fmx-btn-xs-rule" onClick={() => onPromote(bucket, key, item, fieldset || confirmedFieldsets[0])}>
            <i className="fa-solid fa-plus" /> Create as a field anyway
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          <input
            className="fmx-input"
            autoFocus
            style={{ flex: 1, fontSize: 12, padding: '4px 8px' }}
            placeholder="Why? (optional — kept in the session record)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { onDismiss(bucket, key, reason); } if (e.key === 'Escape') setDismissing(false); }}
          />
          <button className="fmx-btn-xs" onClick={() => onDismiss(bucket, key, reason)}>Confirm</button>
          <button className="fmx-btn-xs" onClick={() => setDismissing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

/**
 * @param draft              the editable draft (buckets + overlay lists)
 * @param duplicateFields    [{ field, kind: 'duplicate'|'similar', message }]
 *                           computed by the parent from live validation
 * @param confirmedFieldsets promote targets (scope order)
 * @param liveNames          live names per prerequisite kind (prereqs.js)
 * @param tab / onTab        controlled bucket tab — the needs-attention
 *                           strip's jump links land on a specific bucket
 * @param onDismiss(bucket, key, reason, restore?)
 * @param onPromote(bucket, key, item, fieldset)
 * @param onJumpToField(fieldId)
 * @param onDeleteField(fieldId)  "remove from form" on a duplicate
 */
export default function BucketsPanel({
  draft, duplicateFields, confirmedFieldsets, liveNames, tab, onTab,
  onDismiss, onPromote, onJumpToField, onJumpToFieldName, onDeleteField,
}) {

  const entriesByBucket = useMemo(() => {
    const out = {};
    for (const b of BUCKETS) {
      if (DERIVED_BUCKETS.has(b.id)) continue;
      out[b.id] = bucketItems(draft, b.id);
    }
    return out;
  }, [draft]);

  // Every field carrying a visibility formula, for the copy-one / copy-all
  // handoff (FMX's API can't set these — they go in by hand).
  const formulas = useMemo(() => formulaChecklist(draft?.fields), [draft]);
  const conflicts = draft?.mergeConflicts || [];

  const derivedCount = (id) => id === 'duplicates' ? (duplicateFields || []).length
    : id === 'formulas' ? formulas.length
    : id === 'conflicts' ? conflicts.length
    : null;

  const openCount = (id) => DERIVED_BUCKETS.has(id)
    ? derivedCount(id)
    : (entriesByBucket[id] || []).filter(e => e.status === 'open').length;

  // Default to the first bucket that has anything open.
  const current = tab || BUCKETS.find(b => openCount(b.id) > 0)?.id || 'nativeMappings';

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '10px 10px 8px' }}>
        {BUCKETS.map(b => {
          const n = current === b.id;
          const count = DERIVED_BUCKETS.has(b.id) ? derivedCount(b.id) : (entriesByBucket[b.id] || []).length;
          return (
            <button
              key={b.id}
              onClick={() => onTab(b.id)}
              style={{
                font: `600 11px/15px ${T.fontSans}`, color: n ? T.white : T.inkMuted,
                background: n ? T.brand : T.bgMuted, border: 'none', borderRadius: T.radiusPill,
                padding: '4px 10px', cursor: 'pointer',
              }}
            >
              {b.label}{count > 0 ? ` ${count}` : ''}
            </button>
          );
        })}
      </div>

      <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
        {current === 'formulas' ? (
          formulas.length === 0
            ? <p style={{ color: T.inkSoft, fontSize: 12, padding: '10px 12px' }}>No conditional fields on this form.</p>
            : (
              <div>
                <div style={{ padding: '8px 10px', background: T.tint.info, borderBottom: `1px solid ${T.borderSubtle}` }}>
                  <div style={{ font: `400 11px/16px ${T.fontSans}`, color: T.ink, marginBottom: 6 }}>
                    FMX’s API can’t set visibility formulas, so these are applied by hand: create the
                    fields, then open each one in FMX and paste its formula.
                  </div>
                  <CopyFormulaButton
                    className="fmx-btn-xs-rule"
                    label={`Copy all ${formulas.length} as a checklist`}
                    text={formulas.map(f => `${f.fieldName}: ${f.formula}`).join('\n')}
                  />
                </div>
                {formulas.map((f, i) => (
                  <div key={i} style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderSubtle}` }}>
                    <div style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink }}>{f.fieldName}</div>
                    <div style={{ font: `400 11px/17px ${T.fontData}`, color: T.inkMuted, margin: '3px 0 5px', wordBreak: 'break-all' }}>
                      {f.formula}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <CopyFormulaButton text={f.formula} label="Copy" />
                      <button className="fmx-btn-xs" onClick={() => onJumpToFieldName?.(f.fieldName)}>Go to field</button>
                    </div>
                  </div>
                ))}
              </div>
            )
        ) : current === 'conflicts' ? (
          conflicts.length === 0
            ? <p style={{ color: T.inkSoft, fontSize: 12, padding: '10px 12px' }}>Nothing needs a merge decision.</p>
            : conflicts.map((c, i) => (
              <div key={i} style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderSubtle}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <Badge color="orange">{c.kind === 'options' ? 'different options' : c.kind === 'fieldType' ? 'different types' : 'same section'}</Badge>
                  <span style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink, minWidth: 0 }}>{c.name}</span>
                </div>
                <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted }}>{c.detail}</div>
                {(c.variants || []).map((v, vi) => (
                  <div key={vi} style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkSoft, marginTop: 3 }}>
                    <b>{v.sections.join(', ') || 'no section'}</b>
                    {v.options?.length ? <> — {v.options.join(' · ')}</> : <> — {v.fieldType}</>}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button className="fmx-btn-xs" onClick={() => onJumpToFieldName?.(c.name)}>Go to field</button>
                </div>
              </div>
            ))
        ) : current === 'duplicates' ? (
          (duplicateFields || []).length === 0
            ? <p style={{ color: T.inkSoft, fontSize: 12, padding: '10px 12px' }}>No collisions with fields already on this site.</p>
            : duplicateFields.map(({ field, kind, message }) => (
              <div key={field.id} style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderSubtle}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink, flex: 1, minWidth: 0 }}>{field.name}</span>
                  <Badge color={kind === 'duplicate' ? 'red' : 'orange'}>{kind === 'duplicate' ? 'exists' : 'similar'}</Badge>
                </div>
                <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>{message}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button className="fmx-btn-xs" onClick={() => onJumpToField(field.id)}>Go to field</button>
                  <button className="fmx-btn-xs" onClick={() => onDeleteField(field.id)}>
                    <i className="fa-solid fa-xmark" /> Remove from form
                  </button>
                </div>
              </div>
            ))
        ) : (
          (entriesByBucket[current] || []).length === 0
            ? <p style={{ color: T.inkSoft, fontSize: 12, padding: '10px 12px' }}>Nothing here.</p>
            : (entriesByBucket[current] || []).map(entry => (
              <BucketItemCard
                key={entry.key}
                bucket={current}
                entry={entry}
                confirmedFieldsets={confirmedFieldsets}
                liveNames={liveNames}
                onDismiss={onDismiss}
                onPromote={onPromote}
                onJumpToField={onJumpToField}
              />
            ))
        )}
      </div>
    </div>
  );
}
